import React, { useState } from 'react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok && result.status === 'ok') {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        })
      } else {
        setStatus('error')
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    }
  }

  return (
    <div className="bg-silver-100 border border-silver-400 p-6 md:p-8 rounded-sm shadow-xl backdrop-blur-sm">
      <h3 className="text-xl font-bold text-slate-950 tracking-tight mb-2">Send en besked</h3>
      <p className="text-sm text-slate-700 mb-6">
        Udfyld formularen nedenfor for at modtage et uforpligtende tilbud på dit erhvervsprojekt. Vi vender tilbage hurtigst muligt.
      </p>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-300 p-6 rounded-sm text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-bold text-slate-950 mb-2">Tak for din henvendelse!</h4>
          <p className="text-sm text-slate-800">
            Vi har modtaget din besked og vil behandle din forespørgsel hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 px-4 py-2 bg-silver-300 hover:bg-silver-400 text-slate-950 text-xs font-bold uppercase tracking-wider transition-colors duration-200 rounded-sm cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 relative">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="absolute sr-only -top-9999 -left-9999" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-800 mb-1">
              Navn <span className="text-amber-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-white border border-silver-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-900 px-4 py-3 text-sm rounded-sm transition-colors duration-200 shadow-sm"
              placeholder="Firma / Kontaktperson"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-800 mb-1">
                Telefonnummer <span className="text-amber-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-white border border-silver-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-900 px-4 py-3 text-sm rounded-sm transition-colors duration-200 shadow-sm"
                placeholder="Direkte telefonnummer"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-800 mb-1">
                E-mail <span className="text-amber-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-white border border-silver-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-900 px-4 py-3 text-sm rounded-sm transition-colors duration-200 shadow-sm"
                placeholder="firma@mail.dk"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-slate-800 mb-1">
              Beskrivelse <span className="text-amber-600">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-white border border-silver-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-900 px-4 py-3 text-sm rounded-sm transition-colors duration-200 resize-none shadow-sm"
              placeholder="Beskriv dit projekt, omfang og tidsramme..."
            />
          </div>

          {status === 'error' && (
            <div className="bg-rose-50 border border-rose-300 p-4 rounded-sm text-sm text-rose-800">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-slate-950 font-bold uppercase tracking-wider py-4 px-6 text-sm rounded-sm transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer shadow-md"
          >
            {status === 'submitting' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-950" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sender...
              </>
            ) : (
              'Send forespørgsel'
            )}
          </button>
        </form>
      )}
    </div>
  )
}
